import { Component, computed, effect, ElementRef, inject, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { MaterialModule } from './material/material.module';
import { CommonModule } from '@angular/common';
import { TokenService } from './services/token.service';
import { finalize } from 'rxjs';
import { AppService } from './services/app.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BillingComponent } from './components/billing/billing.component';
import { MatDrawer } from '@angular/material/sidenav';
import { BusinessProfileResponse, JobsService, UpdateBusinessIconResponse } from './services/jobs.service';

interface Navlink {
  title: string;
  link: string;
  allowed_roles: String[]
}

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterModule, RouterOutlet, MaterialModule, BillingComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly _route = inject(Router);
  private readonly _tokenService = inject(TokenService);
  private readonly _appService = inject(AppService)
  private readonly _jobsService = inject(JobsService)
  private readonly _snackbar = inject(MatSnackBar)
  private readonly _router = inject(Router);

  @ViewChild(MatDrawer) drawer!: MatDrawer;
  @ViewChild('businessIconInput') businessIconInput?: ElementRef<HTMLInputElement>;

  routes: Navlink[] = [
    {
      title: 'Users',
      link: '/users',
      allowed_roles: ['admin']
    },
    {
      title: 'Jobs',
      link: '/jobs',
      allowed_roles: ['admin', 'user']
    }
  ]
  navItems: WritableSignal<any[]> = signal([]);
  filteredRoutes = computed(() => {
    const userRole = this._appService.userRole();
    return this.routes.filter(route =>
      route.allowed_roles?.includes(userRole) ?? false
    );
  });
  route = signal('');

  sidenavType: WritableSignal<'buy' | 'billing'> = signal('buy');

  loading: WritableSignal<boolean> = signal(false);
  isLoggedIn: Signal<boolean> = signal(false);
  availableCreds: Signal<number> = signal(0);
  userState = this._appService.userState;
  businessIconUrl: Signal<string | null> = signal(null);
  isUploadingBusinessIcon: WritableSignal<boolean> = signal(false);
  businessIconImageLoadFailed: WritableSignal<boolean> = signal(false);
  showBusinessIconImage = computed(() => !!this.businessIconUrl() && !this.businessIconImageLoadFailed());

  isMobileMenuOpen: WritableSignal<boolean> = signal(false);
  private previewBusinessIconUrl: string | null = null;
  private hasLoadedBusinessProfile = false;
  private readonly acceptedBusinessIconMimeTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/svg+xml',
  ]);
  private readonly maxBusinessIconFileSizeBytes = 2 * 1024 * 1024;

  constructor() {
    this._route.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.route.set(event.url);
      }
    });

    effect(() => {
      const isLoggedIn = this._appService.isUserLoggedIn();

      if (!isLoggedIn) {
        this.hasLoadedBusinessProfile = false;
        this.businessIconImageLoadFailed.set(false);
        this.cleanupPreviewBusinessIcon();
        return;
      }

      if (!this.hasLoadedBusinessProfile) {
        this.hasLoadedBusinessProfile = true;
        this.loadBusinessProfile();
      }
    });
  }

  ngOnInit(): void {
    this.availableCreds = this._appService.availableCreds;
    this.isLoggedIn = this._appService.isUserLoggedIn;
    this.businessIconUrl = this._appService.businessIconUrl;
  }

  ngOnDestroy(): void {
    this.cleanupPreviewBusinessIcon();
  }

  sidenavClasses() {
    return `
      fixed md:relative top-0 left-0 h-full w-64 bg-[#212529]
      transform transition-transform duration-300 z-40
      ${this.isMobileMenuOpen() ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
    `;
  }

  setCurrentPage(page: any) {
    this.closeMobileMenu();
    this._router.navigate([page])
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.set(!this.isMobileMenuOpen());
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }


  logout() {
    this.loading.set(true);
    this._tokenService
      .logout()
      .pipe(finalize(() => {
        this.loading.set(false);
        this._appService.resetState();
        this._route.navigate(['/login']);
      }))
      .subscribe({
        error: () => this._snackbar.open('Unable to logout. Please try again later.'),
      });
  }

  navigateHome() {
    if (this.isLoggedIn()) {
      this._route.navigate(['/jobs']);
    }
  }

  openModal(mode: 'buy' | 'billing') {
    this.sidenavType.set(mode);
    this.drawer.open();
  }

  loadBusinessProfile() {
    this._jobsService.getBusinessProfile().subscribe({
      next: (response: any) => {
        const iconUrl = this.extractIconUrl(response);
        this._appService.updateBusinessName(response.data.business_name);
        this._appService.updateBusinessIcon(iconUrl);
        this.businessIconImageLoadFailed.set(false);
      },
      error: () => {
        this._appService.updateBusinessIcon(null);
      },
    });
  }

  triggerBusinessIconPicker() {
    if (this.isUploadingBusinessIcon()) {
      return;
    }
    this.businessIconInput?.nativeElement.click();
  }

  onBusinessIconSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) {
      return;
    }

    if (!this.acceptedBusinessIconMimeTypes.has(file.type)) {
      this._snackbar.open('Please upload a valid image (PNG, JPG, JPEG, WEBP, or SVG).');
      this.resetBusinessIconInput(target);
      return;
    }

    if (file.size > this.maxBusinessIconFileSizeBytes) {
      this._snackbar.open('Please upload an image smaller than 2MB.');
      this.resetBusinessIconInput(target);
      return;
    }

    const previousIconUrl = this.businessIconUrl();
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      this.cleanupPreviewBusinessIcon();
      this._appService.updateBusinessIcon(base64String);
      this.businessIconImageLoadFailed.set(false);
      this.uploadBusinessIcon(base64String, previousIconUrl);
    };
    reader.readAsDataURL(file);
    this.resetBusinessIconInput(target);
  }

  private toRawBase64(dataUrl: string): string {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) return dataUrl;
    return dataUrl.slice(commaIndex + 1);
  }

  uploadBusinessIcon(file: any, previousIconUrl: string | null) {
    const payload = {
      icon: this.toRawBase64(file)
    }
    this.isUploadingBusinessIcon.set(true);
    this._jobsService
      .updateBusinessIcon(payload)
      .pipe(finalize(() => this.isUploadingBusinessIcon.set(false)))
      .subscribe({
        next: (response) => {
          const iconUrl = this.extractIconUrl(response);

          if (iconUrl) {
            this._appService.updateBusinessIcon(iconUrl);
            this.businessIconImageLoadFailed.set(false);
          } else {
            this._appService.updateBusinessIcon(previousIconUrl);
          }

          this.cleanupPreviewBusinessIcon();
          this._snackbar.open(response?.message || 'Business icon updated successfully.');
        },
        error: () => {
          this._appService.updateBusinessIcon(previousIconUrl);
          this.cleanupPreviewBusinessIcon();
          this._snackbar.open('Unable to update business icon. Please try again.');
        },
      });
  }

  onBusinessIconImageError() {
    this.businessIconImageLoadFailed.set(true);
  }

  private extractIconUrl(response: BusinessProfileResponse | UpdateBusinessIconResponse): string | null {
    const data = response?.data;
    if (!data) {
      return null;
    }

    const icon = data['icon'];
    if (typeof icon === 'string' && icon.trim().length) {
      const trimmedIcon = icon.trim();
      if (trimmedIcon.startsWith('data:image/') || trimmedIcon.startsWith('http')) {
        return trimmedIcon;
      }
      // If it doesn't start with data:image or http, assume it's a raw base64 string
      return `data:image/png;base64,${trimmedIcon}`;
    }

    const iconUrl = data.icon_url;
    return typeof iconUrl === 'string' && iconUrl.trim().length ? iconUrl : null;
  }

  private cleanupPreviewBusinessIcon() {
    if (!this.previewBusinessIconUrl) {
      return;
    }

    URL.revokeObjectURL(this.previewBusinessIconUrl);
    this.previewBusinessIconUrl = null;
  }

  private resetBusinessIconInput(input: HTMLInputElement) {
    input.value = '';
  }
}
